import { humanReadablePrice } from '@/util/currencyFormat'
import { make } from '@/util/Errors'
import { currencies, CurrencyList } from '@/util/tokenCurrency'
import BigNumber from 'bignumber.js'

export interface ExchangeRate {
	exchangeRate: string | number
	usd: string | number | null
	bpx: string | null
	bpx_dec: string | number | null
}

export interface Options {
	showDecimals?: boolean | null
	decimals?: number | null
	commify?: boolean | null
	pad?: boolean | null

	// used for currency displays
	name?: string | null
	name_short?: string | null
	slug?: string | null
}

export const CURRENCY_TYPE_BPX = 'BPX'
export const CURRENCY_TYPE_USD = 'USD'
export const CURRENCY_TYPE_MILES = 'Miles'

export default class Currency {
	protected val: BigNumber
	protected unit: BigNumber
	protected decimals: number = 9
	protected opts: Options | null

	constructor(val: BigNumber.Value, opts: Options = {}) {
		this.val = new BigNumber(val)

		this.opts = opts || {
			decimals: 9,
		}

		if (opts) {
			this.decimals = opts.decimals || 9
		}

		this.unit = new BigNumber(1).multipliedBy(new BigNumber(10).exponentiatedBy(this.decimals))
	}

	add(value: BigNumber.Value): Currency {
		return new Currency(this.val.plus(value), this.opts)
	}

	mul(value: BigNumber.Value): Currency {
		return new Currency(this.val.times(value), this.opts)
	}

	sub(value: BigNumber.Value): Currency {
		return new Currency(this.val.minus(value), this.opts)
	}

	toDecimal(opts: Options | null = null): string {
		const options =
			opts ||
			({
				showDecimals: true,
			} as Options)

		if (options.showDecimals) {
			return this.val.dividedBy(this.unit).toFixed(this.decimals, BigNumber.ROUND_FLOOR)
		}

		return this.val.dividedBy(this.unit).toFixed(0, BigNumber.ROUND_FLOOR)
	}

	static fromDecimal(num: number | string, decimals: number = 9): Currency {
		const unit = new BigNumber(10).pow(decimals)
		const val = new BigNumber(num).multipliedBy(unit)
		return new Currency(val)
	}

	toString() {
		return this.val.toString()
	}

	decimal(): number {
		return this.decimals || this.opts.decimals || 9
	}

	readablePrice(showDecimals: false = false): string {
		return this.toDecimal({ showDecimals })
	}

	toDecimalCurrency() {
		return Currency.fromDecimal(this.val.toString(), this.opts.decimals)
	}

	slug(): string {
		return this.opts.slug || ''
	}

	name(): string {
		return this.opts.name || ''
	}

	nameShort(): string {
		return this.opts.name_short || ''
	}

	static makeBySlug(slug: string, value: number | string = 0) {
		const currency = CurrencyList.find((c) => c.slug === slug)

		if (!currency) {
			throw new Error(`Currency ${slug} not found`)
		}

		let amount
		if (value === null || value === undefined || value === '') {
			amount = new BigNumber(0)
		} else {
			amount = new BigNumber(value)
		}

		return new Currency(amount, {
			decimals: currency.decimals,
			name: currency.name,
			name_short: currency.name_short,
			slug: currency.slug,
		})
	}

	static defaultCurrency() {
		return Currency.makeBySlug('bpx')
	}
}
